<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ skill ? (skill.name || skill.title) : $t('bo.esco.skillDetail.title') }}</h2>
        <button @click="$emit('close')" class="btn-close">&times;</button>
      </div>

      <div v-if="loading" class="modal-body loading-state">{{ $t('bo.common.loading') }}</div>

      <template v-else-if="skill">
        <div class="modal-body">
          <div v-if="error" class="error-banner">{{ error }}</div>
          <div v-if="success" class="success-banner">{{ success }}</div>

          <!-- Read-only info -->
          <div class="info-grid">
            <div class="info-item">
              <label>{{ $t('bo.esco.skillDetail.escoUri') }}</label>
              <span class="mono">{{ skill.esco_uri || '—' }}</span>
            </div>
            <div class="info-item">
              <label>{{ $t('bo.esco.colType') }}</label>
              <span>{{ skill.esco_skill_type || '—' }}</span>
            </div>
            <div class="info-item">
              <label>{{ $t('bo.esco.skillDetail.parentSkill') }}</label>
              <span v-if="skill.parent_title" class="parent-chip">{{ skill.parent_title }}</span>
              <span v-else class="muted">{{ $t('bo.esco.skillDetail.noneRoot') }}</span>
            </div>
            <div class="info-item">
              <label>{{ $t('bo.esco.skillDetail.childrenMerged') }}</label>
              <span class="chip-row">
                <span class="count-chip count-active">{{ $t('bo.esco.skillDetail.activeCount', { count: skill.children_count ?? 0 }) }}</span>
                <span v-if="skill.merged_count > 0" class="count-chip count-merged">{{ $t('bo.esco.skillDetail.mergedCount', { count: skill.merged_count }) }}</span>
              </span>
            </div>
            <div class="info-item">
              <label>{{ $t('bo.esco.skillDetail.skillGroup') }}</label>
              <span>{{ skill.skill_group || '—' }}</span>
            </div>
            <div v-if="skill.manually_edited" class="info-item">
              <label>{{ $t('bo.esco.skillDetail.overrideFlags') }}</label>
              <div class="override-flags">
                <span v-for="(val, key) in (skill.manual_overrides || {})" :key="key" class="flag">{{ key }}</span>
              </div>
            </div>
          </div>

          <!-- Editable fields -->
          <div class="form-group">
            <label>{{ $t('bo.esco.skillDetail.nameEn') }}</label>
            <input v-model="editForm.title_en" type="text" class="input" :placeholder="$t('bo.esco.skillDetail.nameEnPlaceholder')" />
          </div>

          <div class="form-group">
            <label>{{ $t('bo.esco.skillDetail.nameFr') }}</label>
            <input v-model="editForm.title_fr" type="text" class="input" :placeholder="$t('bo.esco.skillDetail.nameFrPlaceholder')" />
          </div>

          <div class="form-group">
            <label>{{ $t('bo.esco.skillDetail.descEn') }}</label>
            <textarea v-model="editForm.description_en" class="textarea" rows="3" />
          </div>

          <div class="form-group">
            <label>{{ $t('bo.esco.skillDetail.descFr') }}</label>
            <textarea v-model="editForm.description_fr" class="textarea" rows="3" />
          </div>

          <!-- Linked occupations (read-only) -->
          <div v-if="skill.occupations && skill.occupations.length" class="linked-section">
            <label>{{ $t('bo.esco.skillDetail.linkedOccupations', { count: skill.occupations.length }) }}</label>
            <table class="occ-table">
              <thead>
                <tr>
                  <th>{{ $t('bo.esco.colTitle') }}</th>
                  <th>{{ $t('bo.esco.skillDetail.colRelation') }}</th>
                  <th>{{ $t('bo.esco.skillDetail.colImportance') }}</th>
                  <th>{{ $t('bo.esco.skillDetail.colLevel') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="occ in skill.occupations.slice(0, 15)" :key="occ.id">
                  <td>{{ occ.title }}</td>
                  <td>
                    <span :class="['rel-badge', `rel-badge--${occ.relation_type}`]">{{ occ.relation_type }}</span>
                  </td>
                  <td>{{ occ.importance != null ? Number(occ.importance).toFixed(2) : '—' }}</td>
                  <td>{{ occ.level != null ? occ.level : '—' }}</td>
                </tr>
              </tbody>
            </table>
            <p v-if="skill.occupations.length > 15" class="occ-more">{{ $t('bo.esco.skillDetail.moreOccupations', { count: skill.occupations.length - 15 }) }}</p>
          </div>

          <!-- Sectors aggregated from linked occupations -->
          <div v-if="skill.sectors && skill.sectors.length" class="linked-section">
            <label>{{ $t('bo.esco.skillDetail.sectorsViaOccupations', { count: skill.sectors.length }) }}</label>
            <div class="sectors-list">
              <span v-for="sec in skill.sectors" :key="sec.id" class="sector-badge">{{ sec.name }}</span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="$emit('close')" class="btn-cancel">{{ $t('bo.common.cancel') }}</button>
          <button @click="save" :disabled="saving" class="btn-submit">
            {{ saving ? $t('bo.common.saving') : $t('bo.common.saveChanges') }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import escoService from '@/services/escoService'

const { t } = useI18n()

const props = defineProps({ skillId: { type: Number, required: true } })
const emit = defineEmits(['close', 'updated'])

const skill = ref(null)
const loading = ref(true)
const saving = ref(false)
const error = ref('')
const success = ref('')

const editForm = ref({ title_en: '', title_fr: '', description_en: '', description_fr: '' })

const loadSkill = async () => {
  loading.value = true
  try {
    const res = await escoService.getSkill(props.skillId)
    skill.value = res.data
    editForm.value = {
      title_en: res.data.title_en || '',
      title_fr: res.data.title_fr || '',
      description_en: res.data.description_en || '',
      description_fr: res.data.description_fr || '',
    }
  } catch {
    error.value = t('bo.esco.skillDetail.failedLoad')
  } finally {
    loading.value = false
  }
}

const save = async () => {
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    await escoService.updateSkill(props.skillId, editForm.value)
    success.value = t('bo.esco.skillDetail.saveSuccess')
    await loadSkill()
    emit('updated')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.esco.skillDetail.failedSave')
  } finally {
    saving.value = false
  }
}

onMounted(loadSkill)
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 620px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.loading-state {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.error-banner {
  background: #fef2f2; border: 1px solid #fecaca; color: #dc2626;
  padding: 0.75rem 1rem; border-radius: 0.375rem; font-size: 0.875rem;
}

.success-banner {
  background: #f0fdf4; border: 1px solid #bbf7d0; color: #166534;
  padding: 0.75rem 1rem; border-radius: 0.375rem; font-size: 0.875rem;
}

.info-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.info-item label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.mono { font-family: monospace; font-size: 0.85rem; color: #374151; }

.override-flags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.flag {
  display: inline-block;
  padding: 0.1rem 0.4rem;
  background: #fef3c7;
  color: #92400e;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  width: 100%;
  box-sizing: border-box;
}

.input:focus { outline: none; border-color: #3b82f6; }

.textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  resize: vertical;
  font-family: inherit;
}

.textarea:focus { outline: none; border-color: #3b82f6; }

.linked-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.linked-section > label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.occ-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}
.occ-table th {
  text-align: left;
  padding: 0.4rem 0.6rem;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}
.occ-table td {
  padding: 0.4rem 0.6rem;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
}

.rel-badge {
  display: inline-block;
  padding: 0.1rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
}
.rel-badge--essential { background: #dbeafe; color: #1e40af; }
.rel-badge--optional  { background: #f3f4f6; color: #6b7280; }

.occ-more {
  font-size: 0.78rem;
  color: #6b7280;
  margin: 0.25rem 0 0;
}

.sectors-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.sector-badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  background: #fef3c7;
  color: #92400e;
  border-radius: 9999px;
  font-size: 0.78rem;
  font-weight: 500;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
}

.btn-cancel:hover { background: #f9fafb; }

.btn-submit {
  padding: 0.5rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
}

.btn-submit:hover:not(:disabled) { background: #2563eb; }
.btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }

.parent-chip {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  background: #ede9fe;
  color: #5b21b6;
  border-radius: 0.25rem;
  font-size: 0.8rem;
  font-weight: 500;
}

.muted { color: #9ca3af; font-size: 0.85rem; }

.chip-row { display: flex; gap: 0.375rem; flex-wrap: wrap; }

.count-chip {
  display: inline-block;
  padding: 0.1rem 0.45rem;
  border-radius: 9999px;
  font-size: 0.72rem;
  font-weight: 600;
}
.count-active { background: #d1fae5; color: #065f46; }
.count-merged { background: #fef3c7; color: #92400e; }
</style>
